import React, { useState, useContext, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _, { size, noop, reject } from 'underscore';
import { Stack, DefaultButton, Label, PrimaryButton, MessageBarType } from '@bingads-webui-react/fabric-control';
import { Panel } from '@bingads-webui-campaign-react/panel';

import { Glyphicon } from '@bingads-webui-react/primitive';
import { ExtensionImagesControl } from '@bingads-webui-campaign-react/extension-images-control';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { Alert } from '@bingads-webui-react/alert';
import { ImageSuggestionCarousel } from '@bingads-webui-campaign-react/image-suggestion-carousel';
import { getAdCreatorInstrumentation } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { SuggestionStatus } from '@bingads-webui-campaign/media-repository';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { AddImageCrudAdComponent } from '@bingads-webui-campaign-react/add-media-panel-crud-ad';
import { useImagePicker, useAssetLibraryImagePicker } from '@bingads-webui-campaign-react/add-image-hooks';
import { RecommendationContext } from '@bingads-webui-campaign/responsive-search-ad-recommendation';
import { getImageExtensionsPaneStyle, overrideImageRecommendationClasses } from './styles/image-extensions-pane-style';
import {
  DescriptionView,
  ImageExtensionsList,
  CreateImageExtensionDialog,
  DeleteImageExtensionDialog,
  ImageRecommendationsPanel,
} from './components/index';

const { imageSources } = constants;
const MaxSuggestionsInCarousel = 15;

export const ImageExtensionsPaneView = ({
  loading,
  setLoading,
  selectedImgExtensionsForAssociation,
  onImgExtensionSelected,
  advertiserCustomerId,
  accountId,
  createImageExtension,
  deleteImageExtension,
  updateImageExtension,
  legacyI18n,
  dataModelUtil,
  imagePath,
  i18n,
  maxImageExtensionsPerAdGroup: limit,
  perfMarker,
  preferenceService,
  permissions,
  oDataConfig,
  suggestionState,
  dispatchSuggestionState,
  suggestionPoller,
  fullUrl,
  urlPrefix,
  onUpdateUrlPrefix,
  onUpdateUrlValue,

  instrumentation,
  adCreatorScenario,
  logActivity,
  dateFormat,
  timeZone,
  activityLogger,
  isAADUser,
  assetLibraryEventHandler,
  serverContext,
}) => {
  const [addImageChildPerfmarker] = useState(() => perfMarker.createChild('imagePicker'));
  const classes = useDefaultStyles(getImageExtensionsPaneStyle);
  const {
    displayImagePicker,
    setDisplayImagePicker,
    displayWebsiteSuggestionTab,
    selectedMediaPanelSource,
    setSelectedMediaPanelSource,
  } = useImagePicker();
  const [displayCreateImageExtView, setDisplayCreateImageExtView] = useState(false);
  const [displayDeleteImageExtView, setDisplayDeleteImageExtView] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageDelete, setSelectedImageDelete] = useState(null);
  const [selectedImageEligibleLayouts, setSelectedImageEligibleLayouts] = useState('');
  const [selectedImageEdit, setSelectedImageEdit] = useState([]);
  const [selectedImageEditIndex, setSelectedImageEditIndex] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const emptyFinalUrl = !fullUrl || fullUrl === `${urlPrefix}://`;

  const reachLimit = limit > 0 && size(selectedImgExtensionsForAssociation) >= limit;

  const { websiteSuggestionCarouselActivity, adCreatorScenario: scenario, editImageActivity } =
    getAdCreatorInstrumentation(instrumentation, adCreatorScenario);

  const onEditImage = (item, index) => {
    editImageActivity.trace('click edit button for image extension in RSA');
    dataModelUtil.getImageListFromMTImagesAsync(item.Images).then((imageList) => {
      setSelectedImageEdit(imageList);
      setSelectedImageEditIndex(index);
      setSelectedImageEligibleLayouts(item.EligibleLayout);
      setShowEditPanel(true);
    });
  };

  const closeEditImageView = () => {
    setShowEditPanel(false);
    setSelectedImageEdit(null);
    setSelectedImageEditIndex(null);
    setSelectedImageEligibleLayouts('');
  };

  const onUpdateImage = () => {
    updateImageExtension(selectedImageEditIndex, selectedImageEdit);
    closeEditImageView();
  };

  const closeCreateImageExtView = () => {
    setDisplayCreateImageExtView(false);
    setSelectedImage(null);
  };

  const closeDeleteImageExtView = () => {
    setDisplayDeleteImageExtView(false);
    setSelectedImageDelete(null);
  };

  // two sources: add image panel / image recommendation carousel
  const onNewImageSelected = ([imagePlacements]) => {
    logActivity(`Under Image extensions: selected image from source ${imagePlacements[0].source}`);
    setSelectedImage(imagePlacements);
    setDisplayImagePicker(false);
    setDisplayCreateImageExtView(true);
  };

  const onDelete = (id) => {
    setSelectedImageDelete(id);
    setDisplayDeleteImageExtView(true);
  };

  const isAllUploadFlowImageUpscaleEnabled = useMemo(() => _.get(permissions, ['dynamic', 'AllUploadFlowImageUpscale']), [permissions]);

  const {
    imagePickerError,
    setImagePickerError,
    clearImagePickerError,
    uploadImage,
    processUploadedImage,
    onProcessUploadComplete,
    minSize,
    minUploadSize,
    supportImageUpscale,
    addImageByUrl,
    getStockImages,
    onSelectStockImage,
    onImageSelected,
    onImagesSelected,
    socialMediaDataSource,
    customMediaSourceConfig,
  } = useAssetLibraryImagePicker({
    cid: advertiserCustomerId.toString(),
    aid: accountId.toString(),
    oDataConfig,
    onNewImageSelected,
    i18n,
    permissions,
    serverContext,
    isAllUploadFlowImageUpscaleEnabled,
  });

  const { suggestion = {}, carousel = {} } = suggestionState;

  const { RSAImageExtensionCrop, RSAImageRecommendationReplace } = permissions && permissions.dynamic;

  const [recommendationContext] = useContext(RecommendationContext);
  const [imagesToSuggest, setImagesToSuggest] = useState([]);
  const [carouselErrorMessage, setCarouselErrorMessage] = useState({});
  const [isSuggestionsPanelOpen, setIsSuggestionsPanelOpen] = useState(false);

  const {
    imageAssets, themes, copilotTracking,
  } = recommendationContext;

  const { carouselState, panelState } = useMemo(() => ({
    carouselState: {
      carousel: {
        isError: _.isEmpty(imageAssets),
        images: imageAssets,
      },
      suggestion: {
        images: imageAssets,
      },
    },
    panelState: {
      panelImages: imageAssets,
      hasMore: false,
    },
  }), [imageAssets]);

  const { suggestion: suggestionFromCopilot, carousel: carouselFromCopilot } = carouselState || {};
  const { images: suggestionImages, inProgress: suggestionInProgress } = suggestionFromCopilot || {};

  useEffect(() => {
    const truncatedImages = _.isArray(suggestionImages) && suggestionImages.slice(0, MaxSuggestionsInCarousel);
    setImagesToSuggest(truncatedImages || []);
  }, [suggestionImages]);

  const messageInfo = RSAImageRecommendationReplace ? carouselErrorMessage : carousel.message;
  const isLoading = RSAImageRecommendationReplace ? suggestionInProgress : suggestion.inProgress;
  const images = (RSAImageRecommendationReplace ? imagesToSuggest : carousel.images) || [];
  const isEmpty = RSAImageRecommendationReplace ? carouselFromCopilot && carouselFromCopilot.isError : !!carousel.isError;

  const onSelectImage = (imageUrl, index, suggestionId) => {
    setLoading(true);

    if (RSAImageRecommendationReplace) {
      addImageByUrl(imageUrl, 'CopilotRecommendation')
        .then((result = {}) => {
          if (result && result.errorMessage) {
            setCarouselErrorMessage({ type: MessageBarType.error, message: result.errorMessage });
          }
          if (copilotTracking) {
            copilotTracking.trackSelectedImageAssets({ ImageAssets: [{ suggestionId, imageId: result.processedImage && result.processedImage[0].idString }] });
          }
        })
        .finally(() => {
          setImagesToSuggest(reject(imagesToSuggest, image => image.fullUrl === imageUrl));
          setLoading(false);
        });
    } else {
      websiteSuggestionCarouselActivity.trace(`CarouselImageSelected: ${index};`);

      addImageByUrl(imageUrl, imageSources.WebsiteCarousel)
        .then((result = {}) => {
          if (result && result.errorMessage) {
            dispatchSuggestionState({
              type: SuggestionStatus.SelectSuggestionError,
              error: { errorMessage: result.errorMessage },
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });

      dispatchSuggestionState({
        type: SuggestionStatus.SelectSuggestionComplete,
        selectedIndex: index,
      });
    }
  };

  const onSelectSeeAll = () => {
    if (RSAImageRecommendationReplace) {
      setIsSuggestionsPanelOpen(true);
    } else {
      displayWebsiteSuggestionTab();
    }
  };

  return (
    <Stack className={classnames('img-ext-pane', classes.container)}>
      {RSAImageExtensionCrop && showEditPanel &&
      <Panel
        isOpen={showEditPanel}
        onClose={() => { closeEditImageView(); }}
        panelClassName={classnames(classes.cropPanel, 'image-crop-panel')}
        i18n={i18n}
        title={i18n.getString(_TL_('Edit Image Extension'))}
        width={1260}
      >
        <div className="image-crop-editor-container">
          <ExtensionImagesControl
            perfMarker={perfMarker}
            i18n={legacyI18n}
            genericImage={`${imagePath}1.91to1.jpg`}
            imageList={selectedImageEdit}
            cid={advertiserCustomerId.toString()}
            aid={accountId.toString()}
            isForVNext
            isStockPhotoEnabled
            oDataConfig={oDataConfig}
            permissions={permissions}
            newI18n={i18n}
            instrumentation={instrumentation}
            preferencesService={preferenceService}
            onImageListChanged={(image) => { setSelectedImageEdit(image); }}
            eligibleLayouts={selectedImageEligibleLayouts}
          />
          <div className={classnames('alert-info')}>
            <Alert
              message={i18n.getString(_TL_('Any edits you make to this image extension will be applied to all campaigns and ad groups that use it.'))}
              level="Info"
              icon={(<div className="alert-image" />)}
              dismissible={false}
            />
          </div>
        </div>
        <div className={classnames([classes.panelFooter, 'panel-footer'])}>
          <div className="action-btn-container">
            <PrimaryButton id="crop-save" onClick={() => { onUpdateImage(); editImageActivity.trace('save for edit image extension in RSA'); }} >{i18n.getString(_TL_('Save'))}</PrimaryButton>
            <DefaultButton id="crop-cancel" onClick={() => { closeEditImageView(); editImageActivity.trace('cancel for edit image extension in RSA'); }}>{i18n.getString(_TL_('Cancel'))}</DefaultButton>
          </div>
        </div>
      </Panel>}
      <Stack className="img-ext-pane-header" horizontal tokens={{ padding: '0px 0px 6px 0px' }}>
        <Label className={classes.labelContainer}>{i18n.getString(_TL_('Image extensions'))}</Label>
        <Stack.Item align="center">
          <HelpPopupInline topic="pop_BA_NewAdCreate_ImageExtensions" ariaLabel={i18n.getString(_TL_('Learn more'))} />
        </Stack.Item>
      </Stack>
      <UiBlocker
        blocking={loading}
        loadingText={i18n.getString(_TL_('Loading ...'))}
      >
        <DescriptionView
          i18n={i18n}
          selectedImgExtensionsForAssociation={selectedImgExtensionsForAssociation}
          limit={limit}
        />

        <div>
          <ImageExtensionsList
            i18n={i18n}
            items={selectedImgExtensionsForAssociation}
            className={classnames(classes.listContainer, 'image-exts-list')}
            onSelect={onImgExtensionSelected}
            onDelete={onDelete}
            onEditImage={onEditImage}
            enableEdit={RSAImageExtensionCrop}
          />
          <Stack horizontal tokens={{ padding: '16px 0' }}>
            <DefaultButton
              onRenderIcon={() => <Glyphicon glyph="ba-Add" className={classes.addIcon} />}
              text={i18n.getString(_TL_('Add image'))}
              disabled={reachLimit}
              onClick={() => {
                setDisplayImagePicker(true);
              }}
              className="add-image-btn"
            />
          </Stack>
        </div>

        <ImageSuggestionCarousel
          isLoading={isLoading || false}
          images={images}
          i18n={i18n}
          isEmpty={isEmpty}
          messageInfo={messageInfo}
          onSelectImage={onSelectImage}
          onSelectSeeAll={onSelectSeeAll}
          isCollapsible
          title={_TL_('Recommendations')}
          helpTopic="pop_BA_NewAdCreate_ImageRecs"
          selectable
          hideData={reachLimit}
          enablePolicyMessage
          showViewAllInHeader={RSAImageRecommendationReplace}
          stylingOverrides={RSAImageRecommendationReplace ? {
            classOverrides: overrideImageRecommendationClasses,
            seeAllLinkText: _TL_('View all'),
          } : {}}

          // turn off toggle for now
          allowToggleSelectable={false}
        />
        {isSuggestionsPanelOpen &&
        <ImageRecommendationsPanel
          i18n={i18n}
          title={_TL_('Recommended images')}
          images={panelState.panelImages}
          themes={themes}
          hasMore={panelState.hasMore}
          loadNextPage={panelState.getMoreImageSuggestions}
          onSelectImage={onSelectImage}
          onDismiss={() => setIsSuggestionsPanelOpen(false)}
        />
      }

        <AddImageCrudAdComponent
          isOpen={displayImagePicker}
          setIsOpen={(newOpen) => {
            setDisplayImagePicker(newOpen);
            if (!newOpen) {
              clearImagePickerError();
            }
          }}
          uploadImage={uploadImage}
          processUploadedImage={processUploadedImage}
          onProcessUploadComplete={onProcessUploadComplete}
          supportImageUpscale={supportImageUpscale}
          minSize={minSize}
          minUploadSize={minUploadSize}
          maxUploadNum={1}
          imageExperienceProps={{
            suggestion,
            suggestionPoller,
            finalUrl: emptyFinalUrl ? '' : fullUrl,
            finalUrlProtocol: `${urlPrefix}://`,
            onUpdateFinalUrl: onUpdateUrlValue,
            onUpdateFinalUrlPrefix: onUpdateUrlPrefix,
            isMultimediaAd: false,
            imageOption: constants.defaultImageOption,
          }}
          addImageByUrl={addImageByUrl}
          managedViewSelectionState={selectedMediaPanelSource}
          updateManagedViewSelectionState={setSelectedMediaPanelSource}
          stockImageConfig={{
            getStockImages,
            onSelectStockImage,
          }}
          socialImageConfig={{
            socialMediaDataSource,
            activityLogger,
          }}
          assetLibraryConfig={{
            cid: advertiserCustomerId.toString(),
            aid: accountId.toString(),
            oDataConfig,
            dateFormat,
            timeZone,
            preferencesService: preferenceService,
            activityLogger,
            onImageSelected,
            onImagesSelected,
            onImageAddError: e => setImagePickerError(e),
          }}
          i18n={i18n}
          cid={advertiserCustomerId.toString()}
          aid={accountId.toString()}
          errorMessage={imagePickerError}
          imageType={null}
          customMediaSourceConfig={customMediaSourceConfig}
          perfMarker={addImageChildPerfmarker}
          permissions={permissions}
          isAADUser={isAADUser}
          eventHandler={assetLibraryEventHandler}
          serverContext={serverContext}
        />

        { displayCreateImageExtView &&
          <CreateImageExtensionDialog
            i18n={i18n}
            createImageExtension={createImageExtension}
            selectedImage={selectedImage}
            onCreateSuccess={closeCreateImageExtView}
            onCancel={closeCreateImageExtView}
            adCreatorScenario={scenario}
            permissions={permissions}
          />
        }

        { displayDeleteImageExtView &&
          <DeleteImageExtensionDialog
            i18n={i18n}
            deleteImageExtension={deleteImageExtension}
            selectedImageDelete={selectedImageDelete}
            onDeleteSuccess={closeDeleteImageExtView}
            onCancel={closeDeleteImageExtView}
          />
        }

        {reachLimit &&
        <div className={classnames('alert-info max-limit', classes.alertBanner)}>
          <Alert
            message={i18n.getString(
                  _TL_('You\'ve reached the max limit of {{limit}} images. Please remove images to see your image recommendations.'),
                  { limit }
                )}
            level="Info"
            icon={(<div className="alert-image" />)}
            dismissible={false}
          />
        </div>
        }
        {emptyFinalUrl &&
          <div className={classnames('alert-info final-url-extension-banner', classes.alertBanner)}>
            <Alert
              message={i18n.getString(_TL_('Enter your Final URL to pull images from your website.'))}
              level="Info"
              icon={(<div className="alert-image" />)}
              dismissible={false}
            />
          </div>
        }
        <div className={classnames('alert-info preview-disclaimer', classes.alertBanner)}>
          <Alert
            message={i18n.getString(_TL_('Your image extension may also serve with Search ads that appear in Audience placements. Image extensions are not guaranteed to serve with your ads every time.'))}
            level="Info"
            icon={(<div className="alert-image" />)}
            dismissible={false}
          />
        </div>
      </UiBlocker>

    </Stack>
  );
};

ImageExtensionsPaneView.propTypes = {
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  selectedImgExtensionsForAssociation: PropTypes.arrayOf(PropTypes.shape({})),
  onImgExtensionSelected: PropTypes.func,
  advertiserCustomerId: PropTypes.number.isRequired,
  accountId: PropTypes.number.isRequired,
  createImageExtension: PropTypes.func.isRequired,
  deleteImageExtension: PropTypes.func.isRequired,
  updateImageExtension: PropTypes.func.isRequired,
  legacyI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  dataModelUtil: PropTypes.shape({}),
  imagePath: PropTypes.string,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  maxImageExtensionsPerAdGroup: PropTypes.number,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  preferenceService: PropTypes.shape({
    setAtUserLevel: PropTypes.func,
    findByNameAtUserLevel: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
    }),
  }),

  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
  }).isRequired,

  suggestionState: PropTypes.shape({
    suggestion: PropTypes.shape({}),
    carousel: PropTypes.shape({}),
  }).isRequired,
  dispatchSuggestionState: PropTypes.func.isRequired,
  suggestionPoller: PropTypes.shape({}).isRequired,

  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }).isRequired,
  adCreatorScenario: PropTypes.shape({
    scenarioName: PropTypes.string,
    scenarioValue: PropTypes.string,
  }),
  logActivity: PropTypes.func,

  fullUrl: PropTypes.string,
  urlPrefix: PropTypes.string,
  onUpdateUrlPrefix: PropTypes.func,
  onUpdateUrlValue: PropTypes.func,
  dateFormat: PropTypes.string,
  timeZone: PropTypes.number,
  activityLogger: PropTypes.shape({}),
  isAADUser: PropTypes.bool,
  assetLibraryEventHandler: PropTypes.shape({}),
  serverContext: PropTypes.shape({}),
};

ImageExtensionsPaneView.defaultProps = {
  loading: false,
  setLoading: noop,
  selectedImgExtensionsForAssociation: [],
  onImgExtensionSelected: noop,
  maxImageExtensionsPerAdGroup: 0,
  permissions: {},
  adCreatorScenario: undefined,
  logActivity: noop,
  fullUrl: '',
  urlPrefix: '',
  onUpdateUrlPrefix: noop,
  onUpdateUrlValue: noop,
  dateFormat: 'M/d/yyyy',
  timeZone: 71,
  activityLogger: {},
  isAADUser: false,
  assetLibraryEventHandler: {},
  dataModelUtil: {},
  imagePath: '',
  serverContext: undefined,
};
